import { useIntl } from "react-intl";

import { SquareContainer, SquareImage, SquareMask } from "./styles";

import Labels from "../Labels";
import Logo from "../Logo";

import {
  getColor,
  pickSquareDefaultPositions,
  pickSquareDefaultSizes,
  style,
} from "../shapes-utils";

export function Square01({
  size = 1080,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });

  const squareSizes = pickSquareDefaultSizes({ shape: 1, size });
  const squarePositions =
    positions ??
    pickSquareDefaultPositions({
      shape: 1,
      size,
    });

  return (
    <SquareContainer size={size} label={label} shape={1}>
      <SquareMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size * 1.25}
        fill="none"
        viewBox="0 0 1080 1350"
      >
        <g clipPath="url(#clip0_2039_3676)">
          <path
            fill={getColor(variantColor).primary}
            d="M1080 0H0v232.261a475 475 0 0 1 31.959 1.299c241.824 17.982 424.184 216.496 407.311 443.391C423.08 894.682 229.312 1059.43 0 1056.49V1350h1080z"
            style={style}
          ></path>
          <path
            fill={getColor(variantColor).secondary}
            fillOpacity={getColor(variantColor).fillOpacity}
            fillRule="evenodd"
            d="M-34.501 1113.86c279.733 20.8 522.129-172.495 541.407-431.743S315.043 195.845 35.311 175.044s-522.13 172.498-541.408 431.745 191.863 486.271 471.596 507.071m3.675-57.77c241.824 17.99 451.538-151.373 468.411-378.269s-165.487-425.41-407.31-443.392c-241.824-17.982-451.539 151.376-468.411 378.272s165.487 425.409 407.31 443.389"
            clipRule="evenodd"
            style={style}
          ></path>
          <path
            fill={getColor(variantColor).secondary}
            fillRule="evenodd"
            d="M426.044 675.678C409.797 894.162 207.48 1058.69-27.584 1041.21c-32.573-2.42-64.008-8.21-93.951-16.96-187.138-53.426-317.517-222.669-303.406-412.432 16.307-219.295 219.402-382.949 453.626-365.532 30.736 2.286 60.482 7.576 88.93 15.538 190.337 51.91 322.619 223.026 308.429 413.854m13.759 1.023c-16.872 226.896-226.586 396.259-468.41 378.269-241.823-17.98-424.182-216.493-407.31-443.389s226.587-396.254 468.41-378.272 424.183 216.496 407.31 443.392"
            clipRule="evenodd"
            style={style}
          ></path>
        </g>
      </SquareMask>

      <Logo
        logoSrc={logoSrc}
        size={squareSizes.logoSize}
        top={squarePositions.logoPosition.y}
        left={squarePositions.logoPosition.x}
      />

      {imageUrl && (
        <SquareImage
          src={imageUrl}
          x_axis={x_axis}
          alt="square01"
          top={squarePositions.imagePosition.y}
          left={squarePositions.imagePosition.x}
          size={squareSizes.imageSize}
        />
      )}

      <Labels
        top={squarePositions.labelsPosition.y}
        left={squarePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={squareSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </SquareContainer>
  );
}

export function Square02({
  size = 1080,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });
  const squareSizes = pickSquareDefaultSizes({ shape: 2, size });
  const squarePositions =
    positions ??
    pickSquareDefaultPositions({
      shape: 2,
      size,
    });

  return (
    <SquareContainer size={size} label={label} shape={2}>
      <SquareMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size * 1.25}
        fill="none"
        viewBox="0 0 1080 1350"
      >
        <g clipPath="url(#clip0_683_3660)">
          <path
            fill={getColor(variantColor).primary}
            fillRule="evenodd"
            d="M1080 0H0v1350h617V354h463z"
            clipRule="evenodd"
            style={style}
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M491.653-86.999C491.653-4.289 424.218 63 341.327 63S191-4.287 191-86.999 258.436-237 341.327-237s150.326 67.293 150.326 150.001"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillRule="evenodd"
            d="M127.156 328.687a3.45 3.45 0 1 1-6.899 0 3.45 3.45 0 0 1 6.899 0m-26.961 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m-26.962 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m-26.335 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m80.258-25.077a3.45 3.45 0 1 1-6.899 0 3.45 3.45 0 0 1 6.899 0m-26.961 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m-26.962 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m-26.335 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m80.258-25.081a3.448 3.448 0 1 1 0-.004zm-26.961 0a3.448 3.448 0 0 1-5.887 2.44 3.45 3.45 0 1 1 5.887-2.444zm-26.962 0a3.449 3.449 0 1 1 0-.004zm-26.335 0a3.449 3.449 0 1 1 0-.004zm80.258-25.081a3.448 3.448 0 1 1 0-.004zm-26.961 0a3.448 3.448 0 0 1-5.887 2.44 3.45 3.45 0 1 1 5.887-2.444zm-26.962 0a3.449 3.449 0 1 1 0-.004zm-26.335 0a3.449 3.449 0 1 1 0-.004z"
            clipRule="evenodd"
          ></path>
          <path
            style={style}
            fill="#F2F2F5"
            fillOpacity="0.04"
            fillRule="evenodd"
            d="M-146.05 810-312 968.61v20.949L-124.13 810zm-147.47 190.22L-94.51 810h21.92l-199.03 190.22zM-42.98 810l-199 190.22h21.92L-21.05 810zm-147.47 190.22L8.55 810h21.94l-199.01 190.22zM60.08 810l-199 190.22h21.93L82.02 810zm-147.46 190.22 199-190.22h21.93l-199 190.22zM163.16 810l-199 190.22h21.92l199-190.22zM15.7 1000.22 214.7 810h21.92l-199 190.22zm51.52 0h21.94l169.33-161.863v-20.968zm73.47 0h-21.94l139.74-133.573v20.968zm29.6 0h21.93l66.27-63.346v-20.962zm51.55 0h21.91l14.74-14.088v-20.948zM-312 919.345-197.59 810h21.93L-312 940.314zM-249.13 810-312 870.094v20.955L-227.2 810zM-312 820.829-300.65 810h21.91L-312 841.784z"
            clipRule="evenodd"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillRule="evenodd"
            d="M-111.261 992.246c-42.104 24.304-60.209 72.054-40.359 106.434 19.85 34.39 70.255 42.58 112.359 18.27s60.208-72.06 40.358-106.44c-19.85-34.377-70.255-42.573-112.358-18.264m102 176.664c-77.19 44.57-169.599 29.55-205.991-33.49-36.392-63.03-3.199-150.571 73.991-195.136 77.19-44.566 169.598-29.541 205.99 33.492s3.2 150.574-73.99 195.134"
            clipRule="evenodd"
          ></path>
        </g>
      </SquareMask>
      <Logo
        logoSrc={logoSrc}
        size={squareSizes.logoSize}
        top={squarePositions.logoPosition.y}
        left={squarePositions.logoPosition.x}
      />
      {imageUrl && (
        <SquareImage
          src={imageUrl}
          x_axis={x_axis}
          top={squarePositions.imagePosition.y}
          left={squarePositions.imagePosition.x}
          size={squareSizes.imageSize}
          alt="square02"
        />
      )}
      <Labels
        top={squarePositions.labelsPosition.y}
        left={squarePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={squareSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </SquareContainer>
  );
}

export function Square03({
  size = 1080,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });
  const squareSizes = pickSquareDefaultSizes({ shape: 3, size });
  const squarePositions =
    positions ??
    pickSquareDefaultPositions({
      shape: 3,
      size,
    });
  return (
    <SquareContainer size={size} label={label} shape={3}>
      <SquareMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size * 1.25}
        fill="none"
        viewBox="0 0 1080 1350"
      >
        <g clipPath="url(#clip0_2035_5982)">
          <path
            fill={getColor(variantColor).primary}
            d="M1080 0H0v1350h448V738.943l137.342-241.998H1080z"
            style={style}
          ></path>
          <path
            stroke={getColor(variantColor).secondary}
            strokeWidth="2"
            d="m132.209 80.248-171 114.906"
            style={style}
          ></path>
          <path
            fill={getColor(variantColor).secondary}
            d="m-111.4 1126.66 366.75-217.135Z"
            style={style}
          ></path>
          <path
            stroke={getColor(variantColor).secondary}
            strokeWidth="2"
            d="m-111.4 1126.66 366.75-217.135"
            style={style}
          ></path>
          <path
            fill={getColor(variantColor).secondary}
            d="M132 822.513 106 839.7v-34.374zM550.645 230.744l-39.655 25.887v-51.775zM240.646 59.858 200.99 85.745V33.971zM897.738 298.972 861 322.955v-47.966z"
            style={style}
          ></path>
          <path
            fill="url(#paint0_linear_2035_5982)"
            d="m1088.02 563.731-1096-641.73v823.985l1096 641.734z"
            style={style}
          ></path>
        </g>
      </SquareMask>

      <Logo
        logoSrc={logoSrc}
        size={squareSizes.logoSize}
        top={squarePositions.logoPosition.y}
        left={squarePositions.logoPosition.x}
      />

      {imageUrl && (
        <SquareImage
          src={imageUrl}
          x_axis={x_axis}
          alt="square03"
          top={squarePositions.imagePosition.y}
          left={squarePositions.imagePosition.x}
          size={squareSizes.imageSize}
        />
      )}

      <Labels
        top={squarePositions.labelsPosition.y}
        left={squarePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={squareSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </SquareContainer>
  );
}

export function Square04({
  size = 1080,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });
  const squareSizes = pickSquareDefaultSizes({ shape: 4, size });
  const squarePositions =
    positions ??
    pickSquareDefaultPositions({
      shape: 4,
      size,
    });
  return (
    <SquareContainer size={size} label={label} shape={4}>
      <SquareMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size * 1.25}
        fill="none"
        viewBox="0 0 1080 1350"
      >
        <g clipPath="url(#clip0_2037_3554)">
          <path
            fill={getColor(variantColor).primary}
            style={style}
            d="M320.99 266.081c64.51 254.15 420.619 519.767 606.625 462.665 51.49-15.805 104.295-54.217 152.385-106.896V1350H0V0h356.764c-46.53 71.9-62.596 160.407-35.774 266.081"
          ></path>
          <path
            fill={getColor(variantColor).secondary}
            style={style}
            d="M488.936 535.937c-80.306-80.24-144.081-175.832-167.946-269.855C294.167 160.407 310.233 71.901 356.763 0H231.809c-16.523 55.324-17.687 118.1.181 188.496 32.183 126.791 136.941 256.436 256.946 347.441"
          ></path>
          <path
            fill={getColor(variantColor).secondary}
            style={style}
            fillRule="evenodd"
            d="M257.837 545.625c-2.852 0-5.164 2.271-5.164 5.071 0 2.801 2.312 5.072 5.164 5.072s5.163-2.271 5.163-5.072c0-2.8-2.311-5.071-5.163-5.071m-37.935 0c-2.852 0-5.164 2.271-5.164 5.071 0 2.801 2.312 5.072 5.164 5.072s5.163-2.271 5.163-5.072c0-2.8-2.311-5.071-5.163-5.071m-37.935 0c-2.851 0-5.163 2.271-5.163 5.071 0 2.801 2.312 5.072 5.163 5.072s5.164-2.271 5.164-5.072c0-2.8-2.312-5.071-5.164-5.071m-37.934 0c-2.852 0-5.164 2.271-5.164 5.071 0 2.801 2.312 5.072 5.164 5.072s5.164-2.271 5.164-5.072c0-2.8-2.312-5.071-5.164-5.071m-37.935 0c-2.851 0-5.163 2.271-5.163 5.071 0 2.801 2.312 5.072 5.163 5.072s5.164-2.271 5.164-5.072c0-2.8-2.312-5.071-5.164-5.071m-37.934 0c-2.853 0-5.164 2.271-5.164 5.071 0 2.801 2.311 5.072 5.163 5.072s5.164-2.271 5.164-5.072c0-2.8-2.311-5.071-5.163-5.071m189.673-29.848c-2.852 0-5.164 2.27-5.164 5.071s2.312 5.071 5.164 5.071 5.163-2.27 5.163-5.071-2.311-5.071-5.163-5.071m-37.935 0c-2.852 0-5.164 2.27-5.164 5.071s2.312 5.071 5.164 5.071 5.163-2.27 5.163-5.071-2.311-5.071-5.163-5.071m-37.935 0c-2.851 0-5.163 2.27-5.163 5.071s2.312 5.071 5.163 5.071 5.164-2.27 5.164-5.071-2.312-5.071-5.164-5.071m-37.934 0c-2.852 0-5.164 2.27-5.164 5.071s2.312 5.071 5.164 5.071 5.164-2.27 5.164-5.071-2.312-5.071-5.164-5.071m-37.935 0c-2.851 0-5.163 2.27-5.163 5.071s2.312 5.071 5.163 5.071 5.164-2.27 5.164-5.071-2.312-5.071-5.164-5.071m-37.934 0c-2.853 0-5.164 2.27-5.164 5.071s2.311 5.071 5.163 5.071 5.164-2.27 5.164-5.071-2.311-5.071-5.163-5.071m189.673-29.848c-2.852 0-5.164 2.27-5.164 5.071s2.312 5.071 5.164 5.071S263 493.801 263 491s-2.311-5.071-5.163-5.071m-37.935 0c-2.852 0-5.164 2.27-5.164 5.071s2.312 5.071 5.164 5.071 5.163-2.27 5.163-5.071-2.311-5.071-5.163-5.071m-37.935 0c-2.851 0-5.163 2.27-5.163 5.071s2.312 5.071 5.163 5.071 5.164-2.27 5.164-5.071-2.312-5.071-5.164-5.071m-37.934 0c-2.852 0-5.164 2.27-5.164 5.071s2.312 5.071 5.164 5.071 5.164-2.27 5.164-5.071-2.312-5.071-5.164-5.071m-37.935 0c-2.851 0-5.163 2.27-5.163 5.071s2.312 5.071 5.163 5.071 5.164-2.27 5.164-5.071-2.312-5.071-5.164-5.071m-37.934 0c-2.853 0-5.164 2.27-5.164 5.071s2.311 5.071 5.163 5.071 5.164-2.27 5.164-5.071-2.311-5.071-5.163-5.071m189.673-29.848c-2.852 0-5.164 2.27-5.164 5.071s2.312 5.071 5.164 5.071 5.163-2.27 5.163-5.071-2.311-5.071-5.163-5.071m-37.935 0c-2.852 0-5.164 2.27-5.164 5.071s2.312 5.071 5.164 5.071 5.163-2.27 5.163-5.071-2.311-5.071-5.163-5.071m-37.935 0c-2.851 0-5.163 2.27-5.163 5.071s2.312 5.071 5.163 5.071 5.164-2.27 5.164-5.071-2.312-5.071-5.164-5.071m-37.934 0c-2.852 0-5.164 2.27-5.164 5.071s2.312 5.071 5.164 5.071 5.164-2.27 5.164-5.071-2.312-5.071-5.164-5.071m-37.935 0c-2.851 0-5.163 2.27-5.163 5.071s2.312 5.071 5.163 5.071 5.164-2.27 5.164-5.071-2.312-5.071-5.164-5.071m-37.934 0c-2.853 0-5.164 2.27-5.164 5.071s2.311 5.071 5.163 5.071 5.164-2.27 5.164-5.071-2.311-5.071-5.163-5.071m189.673-19.706c2.852 0 5.163-2.271 5.163-5.071 0-2.801-2.311-5.072-5.163-5.072s-5.164 2.271-5.164 5.072c0 2.8 2.312 5.071 5.164 5.071m-37.935-10.143c-2.852 0-5.164 2.271-5.164 5.072 0 2.8 2.312 5.071 5.164 5.071s5.163-2.271 5.163-5.071c0-2.801-2.311-5.072-5.163-5.072m-37.935 0c-2.851 0-5.163 2.271-5.163 5.072 0 2.8 2.312 5.071 5.163 5.071s5.164-2.271 5.164-5.071c0-2.801-2.312-5.072-5.164-5.072m-37.934 0c-2.852 0-5.164 2.271-5.164 5.072 0 2.8 2.312 5.071 5.164 5.071s5.164-2.271 5.164-5.071c0-2.801-2.312-5.072-5.164-5.072m-37.935 0c-2.851 0-5.163 2.271-5.163 5.072 0 2.8 2.312 5.071 5.163 5.071s5.164-2.271 5.164-5.071c0-2.801-2.312-5.072-5.164-5.072m-32.771 5.072c0 2.8-2.311 5.071-5.163 5.071S63 434.104 63 431.304c0-2.801 2.311-5.072 5.163-5.072s5.164 2.271 5.164 5.072M-69 58.926c-39.7 0-72 31.722-72 70.712 0 38.989 32.3 70.711 72 70.711s72-31.722 72-70.711c0-38.99-32.3-70.712-72-70.712m0 200.35c-72.784 0-132-58.157-132-129.638S-141.784 0-69 0 63 58.156 63 129.638c0 71.481-59.216 129.638-132 129.638"
            clipRule="evenodd"
          ></path>
        </g>
      </SquareMask>

      <Logo
        logoSrc={logoSrc}
        size={squareSizes.logoSize}
        top={squarePositions.logoPosition.y}
        left={squarePositions.logoPosition.x}
      />

      {imageUrl && (
        <SquareImage
          src={imageUrl}
          x_axis={x_axis}
          alt="square04"
          top={squarePositions.imagePosition.y}
          left={squarePositions.imagePosition.x}
          size={squareSizes.imageSize}
        />
      )}

      <Labels
        top={squarePositions.labelsPosition.y}
        left={squarePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={squareSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </SquareContainer>
  );
}

export function Square05({
  size = 1080,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });
  const squareSizes = pickSquareDefaultSizes({ shape: 5, size });
  const squarePositions =
    positions ??
    pickSquareDefaultPositions({
      shape: 5,
      size,
    });
  return (
    <SquareContainer size={size} label={label} shape={5}>
      <SquareMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size * 1.25}
        fill="none"
        viewBox="0 0 1080 1350"
      >
        <g clipPath="url(#clip0_2036_3674)">
          <path
            style={style}
            fill={getColor(variantColor).primary}
            d="M1080 0H0v1350h1080V803.014c-129.193 88.725-277.09 95.113-397.508-23.373-143.303-141.009-151.23-449.027-52.826-557.916 75.035-83.032 289.277-81.702 450.334-13.868z"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M592.098 284.307c-53.216 42.657-89.773 91.101-97.115 138.888-23.46 152.689 164.788 422.217 375.186 466.378 210.401 44.162 351.371-91.092 395.871-297.822q1.245-5.811 2.4-11.598c-23.92 56.646-64.83 112.187-117.12 163.637-143.3 141.008-325.524 176.857-468.827 35.85-121.399-119.454-145.645-358.764-90.395-495.333M867.411 159.135c124.563 7.257 262.749 53.144 341.779 130.907 30.74 30.256 52.23 61.926 65.6 94.405-19.92-113.639-88.71-200.749-224.22-229.191-55.77-11.707-119.368-9.108-183.159 3.879"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillRule="evenodd"
            d="M1050.84 180.283c-2.85 0-5.17 2.271-5.17 5.071 0 2.801 2.32 5.071 5.17 5.071s5.16-2.27 5.16-5.071-2.31-5.071-5.16-5.071m0-37.32c-2.85 0-5.17 2.271-5.17 5.071 0 2.801 2.32 5.071 5.17 5.071s5.16-2.27 5.16-5.071-2.31-5.071-5.16-5.071m-37.94 0c-2.85 0-5.16 2.271-5.16 5.071 0 2.801 2.31 5.071 5.16 5.071s5.17-2.27 5.17-5.071-2.32-5.071-5.17-5.071m37.94-29.848c-2.85 0-5.17 2.27-5.17 5.071s2.32 5.071 5.17 5.071 5.16-2.27 5.16-5.071-2.31-5.071-5.16-5.071m-37.94 0c-2.85 0-5.16 2.27-5.16 5.071s2.31 5.071 5.16 5.071 5.17-2.27 5.17-5.071-2.32-5.071-5.17-5.071m-37.932 0c-2.852 0-5.164 2.27-5.164 5.071s2.312 5.071 5.164 5.071 5.163-2.27 5.163-5.071-2.312-5.071-5.163-5.071m75.872-29.848c-2.85 0-5.17 2.27-5.17 5.071s2.32 5.071 5.17 5.071 5.16-2.27 5.16-5.07c0-2.802-2.31-5.072-5.16-5.072m-37.94 0c-2.85 0-5.16 2.27-5.16 5.071s2.31 5.071 5.16 5.071 5.17-2.27 5.17-5.07c0-2.802-2.32-5.072-5.17-5.072m-37.932 0c-2.852 0-5.164 2.27-5.164 5.071s2.312 5.071 5.164 5.071 5.163-2.27 5.163-5.07c0-2.802-2.312-5.072-5.163-5.072m-37.935 0c-2.852 0-5.164 2.27-5.164 5.071s2.312 5.071 5.164 5.071 5.164-2.27 5.164-5.07c0-2.802-2.312-5.072-5.164-5.072m113.807-29.849c-2.85 0-5.17 2.271-5.17 5.072 0 2.8 2.32 5.07 5.17 5.07s5.16-2.27 5.16-5.07-2.31-5.072-5.16-5.072m-37.94 0c-2.85 0-5.16 2.271-5.16 5.072 0 2.8 2.31 5.07 5.16 5.07s5.17-2.27 5.17-5.07-2.32-5.072-5.17-5.072m-37.932 0c-2.852 0-5.164 2.271-5.164 5.072 0 2.8 2.312 5.07 5.164 5.07s5.163-2.27 5.163-5.07-2.312-5.072-5.163-5.072m-37.935 0c-2.852 0-5.164 2.271-5.164 5.072 0 2.8 2.312 5.07 5.164 5.07s5.164-2.27 5.164-5.07-2.312-5.072-5.164-5.072m-37.935 0c-2.851 0-5.163 2.271-5.163 5.072 0 2.8 2.312 5.07 5.163 5.07s5.164-2.27 5.164-5.07-2.312-5.072-5.164-5.072m151.742-19.705c2.85 0 5.16-2.27 5.16-5.072 0-2.8-2.31-5.07-5.16-5.07s-5.17 2.27-5.17 5.07 2.32 5.072 5.17 5.072M1012.9 23.57c-2.85 0-5.16 2.27-5.16 5.071s2.31 5.072 5.16 5.072 5.17-2.27 5.17-5.072c0-2.8-2.32-5.07-5.17-5.07m-37.932 0c-2.852 0-5.164 2.27-5.164 5.07s2.312 5.072 5.164 5.072 5.163-2.27 5.163-5.072c0-2.8-2.312-5.07-5.163-5.07m-37.935 0c-2.852 0-5.164 2.27-5.164 5.07s2.312 5.072 5.164 5.072 5.164-2.27 5.164-5.072c0-2.8-2.312-5.07-5.164-5.07m-37.935 0c-2.851 0-5.163 2.27-5.163 5.07s2.312 5.072 5.163 5.072 5.164-2.27 5.164-5.072c0-2.8-2.312-5.07-5.164-5.07m-32.771 5.07c0 2.801-2.312 5.072-5.164 5.072S856 31.443 856 28.641c0-2.8 2.311-5.07 5.163-5.07s5.164 2.27 5.164 5.07M0 989.961c-39.7 0-72 31.719-72 70.709s32.3 70.71 72 70.71 72-31.72 72-70.71-32.3-70.709-72-70.709m0 200.349c-72.784 0-132-58.16-132-129.64S-72.784 931.035 0 931.035s132 58.156 132 129.635c0 71.48-59.216 129.64-132 129.64M189.296 713.99l-14.792 14.528-14.8-14.528-5.704 5.602 14.8 14.527-14.8 14.536 5.704 5.602 14.8-14.536 14.792 14.536 5.704-5.602-14.796-14.536L195 719.592z"
            clipRule="evenodd"
          ></path>
          <path
            fill="#fff"
            fillOpacity="0.1"
            fillRule="evenodd"
            d="M303.656 761.721c4.202 4.128 9.791 6.401 15.734 6.401 5.944 0 11.532-2.273 15.734-6.401l3.437-3.375c3.448-3.385 8.032-5.25 12.907-5.25s9.459 1.865 12.906 5.25l1.533 1.506c4.203 4.127 9.791 6.4 15.734 6.4 5.944 0 11.532-2.273 15.735-6.4l1.222-1.202c7.117-6.987 18.696-6.988 25.813 0 8.676 8.521 22.792 8.521 31.469 0l1.139-1.119c7.119-6.988 18.698-6.988 25.813 0 .78.768 2.048.768 2.828 0a1.94 1.94 0 0 0 0-2.778c-8.676-8.52-22.793-8.519-31.469 0l-1.139 1.12c-7.118 6.991-18.697 6.99-25.813 0-8.676-8.521-22.794-8.521-31.469 0l-1.222 1.2c-3.448 3.386-8.031 5.251-12.907 5.251s-9.459-1.865-12.906-5.251l-1.533-1.504c-4.203-4.128-9.791-6.402-15.734-6.402-5.944 0-11.532 2.274-15.735 6.401l-3.437 3.375c-3.447 3.386-8.031 5.25-12.906 5.25s-9.458-1.864-12.906-5.25l-2.429-2.385c-8.676-8.521-22.793-8.522-31.469 0a1.94 1.94 0 0 0 0 2.777c.78.767 2.048.767 2.828 0 7.118-6.989 18.697-6.988 25.812 0zm0 47.141c4.202 4.128 9.791 6.401 15.734 6.401 5.944 0 11.532-2.273 15.734-6.4l3.437-3.376c3.448-3.385 8.032-5.25 12.907-5.25s9.459 1.865 12.906 5.25l1.533 1.506c4.203 4.127 9.791 6.4 15.734 6.4 5.944 0 11.532-2.273 15.735-6.4l1.222-1.202c7.117-6.987 18.696-6.988 25.813 0 8.676 8.521 22.792 8.521 31.469 0l1.139-1.119c7.119-6.988 18.698-6.988 25.813 0 .78.768 2.048.768 2.828 0a1.94 1.94 0 0 0 0-2.778c-8.676-8.52-22.793-8.519-31.469 0l-1.139 1.12c-7.118 6.991-18.697 6.99-25.813 0-8.676-8.521-22.794-8.521-31.469 0l-1.222 1.2c-3.448 3.387-8.031 5.251-12.907 5.251s-9.459-1.864-12.906-5.251l-1.533-1.504c-4.203-4.128-9.791-6.402-15.734-6.402-5.944 0-11.532 2.274-15.735 6.401l-3.437 3.375c-3.447 3.386-8.031 5.25-12.906 5.25s-9.458-1.864-12.906-5.25l-2.429-2.385c-8.676-8.521-22.793-8.522-31.469 0a1.94 1.94 0 0 0 0 2.777c.78.767 2.048.767 2.828 0 7.118-6.989 18.697-6.988 25.812 0z"
            clipRule="evenodd"
          ></path>
          <g fill={getColor(variantColor).secondary} opacity="0.23">
            <path
              style={style}
              d="m683.81 8.832-.787.606.003.004.014.017.055.07.221.285c.196.255.488.638.865 1.146a146 146 0 0 1 3.22 4.535c2.736 4 6.551 9.927 10.856 17.613 8.612 15.374 19.18 37.783 26.995 65.908 6.415 23.089 10.977 50.034 11.074 80.107q.997-.22 1.999-.436c-.141-30.083-4.716-57.054-11.144-80.188-7.864-28.302-18.499-50.855-27.171-66.338-4.337-7.742-8.183-13.717-10.949-17.76a147 147 0 0 0-3.264-4.598 81 81 0 0 0-1.118-1.472l-.06-.077-.016-.02-.004-.005s-.002-.003-.789.603M563.695 448.657q.029-1.074.06-2.146c-36.789 14.927-76.992 23.882-117.216 27.048-52.74 4.15-105.438-1.665-150.399-16.986-44.968-15.323-82.126-40.129-103.906-73.911-44.022-68.281-92.14-87.919-129.374-90.369-18.587-1.223-34.408 1.841-45.583 5.211-5.588 1.685-10.018 3.447-13.057 4.792a75 75 0 0 0-3.485 1.641c-.398.2-.7.36-.906.469l-.235.127-.061.034-.021.012s-.003.001.495.853l.427.733.07.119.002-.001.012-.007.052-.028.215-.116q.289-.156.864-.448a74 74 0 0 1 3.393-1.598c2.972-1.314 7.324-3.047 12.822-4.705 11-3.316 26.572-6.332 44.862-5.128 36.52 2.403 84.094 21.642 127.818 89.46 22.094 34.268 59.691 59.297 104.94 74.716 45.256 15.421 98.236 21.257 151.214 17.088 40.077-3.154 80.198-12.039 116.997-26.86M634.105 739.432q.519.852 1.041 1.702c-47.762 19.083-88.386 50.838-110.077 95.881-34.798 72.263-31.144 134.755-18.81 179.175 6.169 22.22 14.512 39.92 21.308 52.06 3.398 6.07 6.409 10.75 8.568 13.91 1.079 1.58 1.945 2.78 2.539 3.58.297.4.527.7.681.9.077.1.135.18.174.23l.043.05.01.02h.002l-.787.6c-.787.61-.789.61-.789.61l-.003-.01-.013-.01-.048-.06a5 5 0 0 0-.185-.24q-.243-.315-.702-.93c-.608-.82-1.489-2.04-2.583-3.64-2.188-3.21-5.23-7.93-8.659-14.06-6.858-12.25-15.267-30.1-21.485-52.49-12.44-44.802-16.102-107.785 18.931-180.534 21.911-45.499 62.835-77.502 110.844-96.744M1015.22 861.7c1-.465 2.01-.936 3.01-1.411 3.88.71 7.64 1.159 11.26 1.397 14.42.95 26.7-1.429 35.37-4.046 4.33-1.308 7.76-2.674 10.11-3.711 1.17-.518 2.06-.953 2.67-1.258.3-.152.52-.271.67-.352.08-.04.14-.07.17-.09l.04-.022.01-.005v-.001l.5.852c.16.281.27.469.35.596l.15.257-.01.002-.01.008-.05.027-.07.036q-.045.027-.12.065-.24.133-.72.374c-.63.319-1.56.769-2.76 1.302-2.41 1.066-5.92 2.462-10.34 3.798-8.85 2.669-21.38 5.097-36.09 4.128a101 101 0 0 1-14.14-1.946"
            ></path>
          </g>
        </g>
      </SquareMask>

      <Logo
        logoSrc={logoSrc}
        size={squareSizes.logoSize}
        top={squarePositions.logoPosition.y}
        left={squarePositions.logoPosition.x}
      />

      {imageUrl && (
        <SquareImage
          src={imageUrl}
          x_axis={x_axis}
          alt="square05"
          top={squarePositions.imagePosition.y}
          left={squarePositions.imagePosition.x}
          size={squareSizes.imageSize}
        />
      )}

      <Labels
        top={squarePositions.labelsPosition.y}
        left={squarePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={squareSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </SquareContainer>
  );
}

export function Square06({
  size = 1080,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });
  const squareSizes = pickSquareDefaultSizes({ shape: 6, size });
  const squarePositions =
    positions ??
    pickSquareDefaultPositions({
      shape: 6,
      size,
    });
  return (
    <SquareContainer size={size} label={label} shape={6}>
      <SquareMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size * 1.25}
        fill="none"
        viewBox="0 0 1080 1350"
      >
        <g clipPath="url(#clip0_2039_3628)">
          <path
            style={style}
            fill={getColor(variantColor).primary}
            d="M0 1042.17c20.272-28.41 47.012-83.101 67.37-186.596 18.07-91.861 81.48-107.755 147.245-124.24 71.297-17.87 145.36-36.434 167.418-153.229 42.408-224.534 64.463-271.815 237.41-306.572C740.482 247.206 763.229 91.361 765.98 0H1080v1350H0z"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillRule="evenodd"
            d="M784.104 127.403a559 559 0 0 0 10.601-54.98c7.99-55.673 6.844-100.112 6.827-100.693l-.038-1.33-823.405-138.094-1.462-.245L-196.19 1036.25l134.192 13.59.281.03.273-.09c.639-.21 15.88-5.45 37.294-34.04 19.586-26.147 48.302-77.795 73.872-175.363 23.419-89.356 86.448-100.24 153.178-111.763h.001c72.774-12.566 148.025-25.561 177.488-141.562 29.444-115.922 49.378-178.142 83.034-218.204 32.53-38.723 78.928-57.374 171.187-68.818 40.189-4.985 73.987-25.424 100.457-60.752 21.107-28.17 37.604-65.814 49.036-111.875M-26.103 1013.3c-19.434 26.07-33.847 32.47-35.889 33.27l-130.811-13.24L-20.937-164.235 798.624-26.786c.168 10.49.554 81.275-17.367 153.443-17.759 71.507-57.663 159.071-146.928 170.144-47.026 5.832-79.702 12.977-105.95 23.163-28.205 10.944-49.527 25.783-67.092 46.693-34.077 40.564-54.148 103.12-83.724 219.563-28.942 113.951-103.227 126.779-175.066 139.184-34.035 5.876-66.183 11.427-93.715 27.052-30.877 17.521-51.117 46-61.88 87.067C21.57 936.186-6.782 987.378-26.103 1013.3"
            clipRule="evenodd"
          ></path>
          <ellipse
            style={style}
            cx="194"
            cy="927.106"
            fill={getColor(variantColor).secondary}
            rx="16"
            ry="15.714"
          ></ellipse>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M760.807 397.238c-.896-6.295 2.157-12.5 7.735-15.722s12.583-2.826 17.749 1.003 7.474 10.336 5.849 16.488-6.863 10.737-13.272 11.617c-8.751 1.201-16.837-4.792-18.061-13.386"
            opacity="0.502"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M946.155 944.995c-.897-6.294 2.156-12.499 7.735-15.721 5.578-3.222 12.583-2.826 17.749 1.002s7.474 10.336 5.849 16.489c-1.625 6.152-6.863 10.737-13.272 11.617-8.751 1.201-16.838-4.792-18.061-13.387"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).tertiary}
            fillOpacity="0.1"
            fillRule="evenodd"
            d="M320.656 857.967c4.202 4.128 9.791 6.401 15.734 6.401 5.944 0 11.532-2.273 15.734-6.4l3.437-3.376c3.448-3.385 8.032-5.25 12.907-5.25s9.459 1.865 12.906 5.25l1.533 1.506c4.203 4.127 9.791 6.4 15.734 6.4 5.944 0 11.532-2.273 15.735-6.4l1.222-1.201c7.117-6.988 18.696-6.989 25.813 0 8.676 8.52 22.792 8.52 31.469 0l1.139-1.12c7.119-6.988 18.698-6.988 25.813 0 .78.768 2.048.768 2.828 0a1.94 1.94 0 0 0 0-2.777c-8.676-8.521-22.793-8.52-31.469 0l-1.139 1.119c-7.118 6.991-18.697 6.99-25.813 0-8.676-8.521-22.794-8.521-31.469 0l-1.222 1.2c-3.448 3.387-8.031 5.251-12.907 5.251s-9.459-1.864-12.906-5.251l-1.533-1.504c-4.203-4.128-9.791-6.402-15.734-6.402-5.944 0-11.532 2.274-15.735 6.401l-3.437 3.375c-3.447 3.387-8.031 5.251-12.906 5.251s-9.458-1.864-12.906-5.251l-2.429-2.385c-8.676-8.521-22.793-8.522-31.469 0a1.94 1.94 0 0 0 0 2.777c.78.767 2.048.767 2.828 0 7.118-6.989 18.697-6.988 25.812 0zm0 47.141c4.202 4.128 9.791 6.401 15.734 6.401 5.944 0 11.532-2.273 15.734-6.4l3.437-3.376c3.448-3.385 8.032-5.25 12.907-5.25s9.459 1.865 12.906 5.25l1.533 1.506c4.203 4.127 9.791 6.4 15.734 6.4 5.944 0 11.532-2.273 15.735-6.4l1.222-1.201c7.117-6.988 18.696-6.989 25.813 0 8.676 8.52 22.792 8.52 31.469 0l1.139-1.12c7.119-6.988 18.698-6.988 25.813 0 .78.768 2.048.768 2.828 0a1.94 1.94 0 0 0 0-2.777c-8.676-8.521-22.793-8.52-31.469 0l-1.139 1.119c-7.118 6.991-18.697 6.99-25.813 0-8.676-8.521-22.794-8.521-31.469 0l-1.222 1.2c-3.448 3.387-8.031 5.251-12.907 5.251s-9.459-1.864-12.906-5.251l-1.533-1.504c-4.203-4.128-9.791-6.402-15.734-6.402-5.944 0-11.532 2.274-15.735 6.401l-3.437 3.375c-3.447 3.387-8.031 5.251-12.906 5.251s-9.458-1.864-12.906-5.251l-2.429-2.385c-8.676-8.521-22.793-8.522-31.469 0a1.94 1.94 0 0 0 0 2.777c.78.767 2.048.767 2.828 0 7.118-6.989 18.697-6.988 25.812 0z"
            clipRule="evenodd"
          ></path>
          <ellipse
            style={style}
            cx="1080.5"
            cy="356.505"
            stroke={getColor(variantColor).secondary}
            strokeWidth="50"
            rx="134.5"
            ry="124.727"
          ></ellipse>
        </g>
      </SquareMask>

      <Logo
        logoSrc={logoSrc}
        size={squareSizes.logoSize}
        top={squarePositions.logoPosition.y}
        left={squarePositions.logoPosition.x}
      />

      {imageUrl && (
        <SquareImage
          src={imageUrl}
          x_axis={x_axis}
          alt="square06"
          top={squarePositions.imagePosition.y}
          left={squarePositions.imagePosition.x}
          size={squareSizes.imageSize}
        />
      )}

      <Labels
        top={squarePositions.labelsPosition.y}
        left={squarePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={squareSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </SquareContainer>
  );
}

export function Square07({
  size = 1080,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });
  const squareSizes = pickSquareDefaultSizes({ shape: 7, size });
  const squarePositions =
    positions ??
    pickSquareDefaultPositions({
      shape: 7,
      size,
    });
  return (
    <SquareContainer size={size} label={label} shape={7}>
      <SquareMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size * 1.25}
        fill="none"
        viewBox="0 0 1080 1350"
      >
        <g clipPath="url(#clip0_2042_5057)">
          <path
            fill={getColor(variantColor).primary}
            d="M0 0h1080v10.93c-5.21-.47-10.46-.708-15.74-.708-45.63 0-89.382 17.818-121.617 49.526L564.34 431.172q-.591.58-1.176 1.163l-1.322 1.298c-32.296 31.65-50.444 74.612-50.444 119.413s18.149 87.763 50.445 119.413l260.923 256.178c32.235 31.708 75.992 49.526 121.622 49.526s89.382-17.818 121.622-49.525l13.99-13.736V1350H0z"
            style={style}
          ></path>
          <path
            fill={getColor(variantColor).secondary}
            d="m932.375 17.796-144.25 141.627C763.716 183.344 750 215.814 750 249.675q0 1.356.029 2.709l194.997-191.45c32.235-31.708 75.994-49.526 121.624-49.526 17.35 0 34.44 2.578 50.72 7.518l-1.15-1.13c-24.36-23.966-57.43-37.433-91.92-37.433-34.489 0-67.561 13.467-91.925 37.433"
            style={style}
          ></path>
          <path
            stroke={getColor(variantColor).curvedLine}
            strokeWidth="150"
            d="m-424.834 813.163-293-498.264c-27.899-47.345-35.475-103.641-21.056-156.475s49.649-97.869 97.921-125.175l336.018-190.472c48.222-27.393 105.56-34.83 159.373-20.673s99.681 48.745 127.493 96.14l292.999 498.264c27.9 47.345 35.475 103.64 21.056 156.475s-49.648 97.869-97.921 125.174L-137.969 888.63c-48.221 27.392-105.559 34.83-159.372 20.673s-99.681-48.746-127.493-96.14Z"
            clipRule="evenodd"
            opacity={getColor(variantColor).curvedLineOpacity}
            style={style}
          ></path>
          <path
            fill={getColor(variantColor).quaternary}
            d="m619.075-257.036 507.495-287.672c48.22-27.392 105.56-34.83 159.37-20.673s99.68 48.746 127.49 96.141l194 329.907c27.9 47.345 35.48 103.64 21.06 156.475s-49.65 97.868-97.92 125.174l-165.08 93.573-2.75-2.703 163.77-92.773c47.84-27.046 82.75-71.65 97.04-123.982 14.29-52.33 6.79-108.089-20.86-154.982l-192.28-326.764c-27.56-46.943-73.02-81.202-126.35-95.224s-110.16-6.655-157.95 20.476l-502.972 284.93c-47.843 27.045-82.76 71.65-97.051 123.981-14.292 52.33-6.785 108.09 20.866 154.984l158.136 268.755-3.364 3.303L542.204 24.613C514.305-22.732 506.73-79.028 521.15-131.863s49.651-97.868 97.925-125.173"
            opacity="0.102"
            style={style}
          ></path>
          <path
            fill={getColor(variantColor).quaternary}
            d="m697.075-379.762 507.495-287.672c48.22-27.393 105.56-34.83 159.37-20.673s99.68 48.745 127.49 96.14l194 329.908c27.9 47.345 35.48 103.64 21.06 156.474s-49.65 97.869-97.92 125.174l-165.08 93.573-2.75-2.703 163.77-92.773c47.84-27.045 82.75-71.65 97.04-123.981s6.79-108.089-20.86-154.983l-192.28-326.763c-27.56-46.943-73.02-81.202-126.35-95.224s-110.16-6.655-157.95 20.476l-502.972 284.93c-47.843 27.045-82.76 71.649-97.051 123.98-14.292 52.331-6.785 108.09 20.866 154.984l158.136 268.756-3.364 3.302L620.204-98.114c-27.899-47.345-35.474-103.641-21.054-156.475s49.651-97.868 97.925-125.173"
            opacity="0.102"
            style={style}
          ></path>
        </g>
      </SquareMask>

      <Logo
        logoSrc={logoSrc}
        size={squareSizes.logoSize}
        top={squarePositions.logoPosition.y}
        left={squarePositions.logoPosition.x}
      />

      {imageUrl && (
        <SquareImage
          src={imageUrl}
          x_axis={x_axis}
          alt="square07"
          top={squarePositions.imagePosition.y}
          left={squarePositions.imagePosition.x}
          size={squareSizes.imageSize}
        />
      )}

      <Labels
        top={squarePositions.labelsPosition.y}
        left={squarePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={squareSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </SquareContainer>
  );
}

export function Square08({
  size = 1080,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });
  const squareSizes = pickSquareDefaultSizes({ shape: 8, size });
  const squarePositions =
    positions ??
    pickSquareDefaultPositions({
      shape: 8,
      size,
    });
  return (
    <SquareContainer size={size} label={label} shape={8}>
      <SquareMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size * 1.25}
        fill="none"
        viewBox="0 0 1080 1350"
      >
        <g clipPath="url(#clip0_2042_5188)">
          <path
            fill={getColor(variantColor).primary}
            d="M1080 0H0v1350h538.219C459.77 1222.078 415 1074.306 415 916.903c0-393.123 279.259-726.159 665-839.945z"
            style={style}
          ></path>
          <path
            fill={getColor(variantColor).secondary}
            fillOpacity="0.1"
            fillRule="evenodd"
            d="M1189 1350h496.08c52.36-126.984 81.92-271.053 81.92-423.75 0-511.554-331.76-926.25-741-926.25-409.243 0-741 414.696-741 926.25 0 152.698 29.56 296.766 81.917 423.75h171.302C459.77 1222.078 415 1074.306 415 916.903 415 487.624 747.989 129.993 1189 51.25z"
            clipRule="evenodd"
            style={style}
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M101.039 838.5c0-2.5 2-4.5 4.5-4.5s4.5 2 4.5 4.5-2 4.5-4.5 4.5-4.5-2-4.5-4.5m124.5 4.5c2.5 0 4.5-2 4.5-4.5s-2-4.5-4.5-4.5-4.5 2-4.5 4.5 2 4.5 4.5 4.5m-180-9c-2.5 0-4.5 2-4.5 4.5s2 4.5 4.5 4.5 4.5-2 4.5-4.5-2-4.5-4.5-4.5m120 9c2.5 0 4.5-2 4.5-4.5s-2-4.5-4.5-4.5-4.5 2-4.5 4.5 2 4.5 4.5 4.5m60-45c2.5 0 4.5-2 4.5-4.5s-2-4.5-4.5-4.5-4.5 2-4.5 4.5 2 4.5 4.5 4.5m-60 0c2.5 0 4.5-2 4.5-4.5s-2-4.5-4.5-4.5-4.5 2-4.5 4.5 2 4.5 4.5 4.5m-60 0c2.5 0 4.5-2 4.5-4.5s-2-4.5-4.5-4.5-4.5 2-4.5 4.5 2 4.5 4.5 4.5m-60 0c2.5 0 4.5-2 4.5-4.5s-2-4.5-4.5-4.5-4.5 2-4.5 4.5 2 4.5 4.5 4.5M285.155 864.216a15.999 15.999 0 0 1 25.484-14.987 16 16 0 0 1-7.423 28.617c-8.751 1.223-16.838-4.879-18.061-13.63"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillRule="evenodd"
            d="M-31 922c-39.7 0-72 32.3-72 72s32.3 72 72 72 72-32.3 72-72-32.3-72-72-72m0 204c-72.784 0-132-59.22-132-132 0-72.784 59.216-132 132-132s132 59.216 132 132c0 72.78-59.216 132-132 132"
            clipRule="evenodd"
          ></path>
        </g>
      </SquareMask>

      <Logo
        logoSrc={logoSrc}
        size={squareSizes.logoSize}
        top={squarePositions.logoPosition.y}
        left={squarePositions.logoPosition.x}
      />

      {imageUrl && (
        <SquareImage
          src={imageUrl}
          x_axis={x_axis}
          alt="square08"
          top={squarePositions.imagePosition.y}
          left={squarePositions.imagePosition.x}
          size={squareSizes.imageSize}
        />
      )}

      <Labels
        top={squarePositions.labelsPosition.y}
        left={squarePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={squareSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </SquareContainer>
  );
}

export function Square09({
  size = 1080,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });
  const squareSizes = pickSquareDefaultSizes({ shape: 9, size });
  const squarePositions =
    positions ??
    pickSquareDefaultPositions({
      shape: 9,
      size,
    });
  return (
    <SquareContainer size={size} label={label} shape={9}>
      <SquareMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size * 1.25}
        fill="none"
        viewBox="0 0 1080 1350"
      >
        <path
          fill={getColor(variantColor).secondary}
          style={style}
          fillRule="evenodd"
          d="M654.48 1350 267.427 675 654.48 0H0v1350z"
          clipRule="evenodd"
        ></path>
        <path
          fill={getColor(variantColor).primary}
          style={style}
          fillRule="evenodd"
          d="M426.359 1195 680.4 675.001 426.359 155H0v1040z"
          clipRule="evenodd"
        ></path>
        <path
          fill={getColor(variantColor).secondary}
          style={style}
          fillOpacity="0.1"
          d="M342.655 242.359 303 268.718V216zM315 820.42l36.738 24.42V796z"
        ></path>
      </SquareMask>

      <Logo
        logoSrc={logoSrc}
        size={squareSizes.logoSize}
        top={squarePositions.logoPosition.y}
        left={squarePositions.logoPosition.x}
      />

      {imageUrl && (
        <SquareImage
          src={imageUrl}
          x_axis={x_axis}
          alt="square09"
          top={squarePositions.imagePosition.y}
          left={squarePositions.imagePosition.x}
          size={squareSizes.imageSize}
        />
      )}

      <Labels
        top={squarePositions.labelsPosition.y}
        left={squarePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={squareSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </SquareContainer>
  );
}

export function Square10({
  size = 1080,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });
  const squareSizes = pickSquareDefaultSizes({ shape: 10, size });
  const squarePositions =
    positions ??
    pickSquareDefaultPositions({
      shape: 10,
      size,
    });
  return (
    <SquareContainer size={size} label={label} shape={10}>
      <SquareMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size * 1.25}
        fill="none"
        viewBox="0 0 1080 1350"
      >
        <g clipPath="url(#clip0_2033_5827)">
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillOpacity="0.2"
            d="M135.655 212.359 96 238.718V186zM351.738 820.42 315 844.84V796z"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).primary}
            d="M725 1350H0V0h485.702z"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillOpacity={getColor(variantColor).triangleOpacity}
            d="M212.76 1413h-724.68V412h485.488z"
          ></path>
        </g>
      </SquareMask>

      <Logo
        logoSrc={logoSrc}
        size={squareSizes.logoSize}
        top={squarePositions.logoPosition.y}
        left={squarePositions.logoPosition.x}
      />

      {imageUrl && (
        <SquareImage
          src={imageUrl}
          x_axis={x_axis}
          alt="square10"
          top={squarePositions.imagePosition.y}
          left={squarePositions.imagePosition.x}
          size={squareSizes.imageSize}
        />
      )}

      <Labels
        top={squarePositions.labelsPosition.y}
        left={squarePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={squareSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </SquareContainer>
  );
}
